import React from "react";
import Image from "next/image";
import imageUrlBuilder from "@sanity/image-url";
import { PortableText, PortableTextComponents } from "@portabletext/react";
import { merge, cloneDeep } from "lodash-es";
import { getImageDimensions } from "@sanity/asset-utils";
import { prodClient } from "./lib/client";
import { cn, sanityImageUrl } from "@/lib/utils";
import chart from "@/public/icons/chat.svg";
import { cubicBezier, motion } from "framer-motion";

const sanityImageBuilder = imageUrlBuilder(prodClient);
// { title: "Green background half", value: "greenBackgroundHalf" },
// { title: "Red background half", value: "redBackgroundHalf" },
// { title: "Grey background half", value: "greyBackgroundHalf" },
// { title: "Green Text", value: "greenText" },

export const richTextComponent: PortableTextComponents = {
  marks: {
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    iconHighlight: ({ children }) => (
      <div className="inline-flex border-b border-b-[#FF5500]">
        <div className="flex justify-center items-center w-[8.25rem] h-[8.25rem] bg-[#FF5500]">
          <Image
            src={chart}
            alt=""
            className="absolute"
          />
        </div>
        <span className="text-[#FF5500] bg-[#FF5500]/10 leading-[100%] px-[1.5rem]">{children}</span>
      </div>
    ),

    orangeHighlight: ({ children }) => (
      <div className="inline-flex border-b border-b-[#FF5500]">
        <span className="text-[#FF5500] bg-[#FF5500]/10 leading-[100%] px-[1.5rem]">{children}</span>
      </div>
    ),

    underline: ({ children }) => (
      <span className="underline decoration-from-font [text-underline-offset:from-font] [text-decoration-skip-ink:none]">
        {children}
      </span>
    ),
    blueUnderline: ({ children }) => (
      <span className="text-[#B5D1E2] underline decoration-[#B5D1E2] decoration-from-font [text-underline-offset:from-font] [text-decoration-skip-ink:none]">
        {children}
      </span>
    ),
    em: ({ children }) => <em className="pr-[.125em]">{children}</em>,
    inlineImage: ({ value }) => {
      const url = value?.image?.asset?._ref ? sanityImageUrl(value?.image?.asset?._ref) : "";
      const dimensions = value?.image?.asset?._ref
        ? getImageDimensions(value?.image?.asset?._ref)
        : {
            width: 0,
            height: 0,
            aspectRatio: 0,
          };
      return (
        <Image
          className="inline-block mx-2 inline-icon"
          src={{
            src: url,
            width: dimensions.width,
            height: dimensions.height,
          }}
          alt="Icon image"
          quality={100}
        />
      );
    },
    inlineCornerChip: ({ value, children }) => {
      const imageUrl = value?.image?.asset?._ref ? sanityImageUrl(value?.image?.asset?._ref) : null;
      const text = value?.text || "";

      return (
        <span className="rt-inlineCornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <span className="font-sans leading-none text-black text-[2rem] translate-y-[.125rem]">{text}</span>
        </span>
      );
    },
  },
  block: {
    h1: ({ children }) => <h1 className="">{children}</h1>,
    h2: ({ children }) => <h2 className="">{children}</h2>,
    h3: ({ children }) => <h3 className="">{children}</h3>,
    h4: ({ children }) => <h4 className="">{children}</h4>,
    h5: ({ children }) => <h5 className="">{children}</h5>,
    h6: ({ children }) => <h6 className="">{children}</h6>,
    normal: ({ children }) => <p className="p-normal">{children}</p>,
  },
  types: {
    image: ({ value }) => {
      return (
        <Image
          className="rounded-[1.25rem]"
          src={{
            src: sanityImageBuilder.image(value).url(),
            width: getImageDimensions(value)?.width,
            height: getImageDimensions(value)?.height,
          }}
          alt={value?.alt || ""}
        />
      );
    },
    solidLine: ({ value }) => {
      const marginClass = value?.marginSize || "my-[4rem]";

      return <hr className={`w-full border-t border-white border-opacity-20 ${marginClass}`} />;
    },
    solidDecoratedLine: ({ value }) => {
      const marginClass = value?.marginSize || "my-[5rem]";
      const bgColor = value?.color || "#153444";

      return (
        <div className={cn("w-full relative flex items-center opacity-[.15]", marginClass)}>
          <div
            className="flex-shrink-0 rotate-45 w-[.625rem] h-[.625rem]"
            style={{ backgroundColor: bgColor }}
          />
          <hr
            className="grow border-t !m-0"
            style={{
              borderColor: bgColor,
            }}
          />
          <div
            className="flex-shrink-0 rotate-45 w-[.625rem] h-[.625rem]"
            style={{ backgroundColor: bgColor }}
          />
        </div>
      );
    },
    dashedLine: ({ value }) => {
      const marginClass = value?.marginSize || "my-[5rem]";

      return <hr className={`w-full border-t border-dashed border-white border-opacity-20 ${marginClass}`} />;
    },
    cornerList: ({ value }) => {
      return (
        <ul className="flex flex-col items-start p-0 list-none cornerList mb-[1.25em] gap-[1rem]">
          {value?.items?.map((item: any, index: number) => {
            return (
              <li
                key={index}
                className="flex items-center p-0 m-0 gap-[1rem]">
                <div className="cornerListCorner bg-[#112628] flex-shrink-0 w-[1.5rem] h-[1.5rem] [clip-path:polygon(0_0,calc(100%-.75rem)_0,100%_.75rem,100%_100%,0_100%)]" />
                <div className="richtext richtext-start prose-p:leading-[1.1em]">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      );
    },
    cornerChip: ({ value }) => {
      const imageUrl = value?.image?.asset?._ref ? sanityImageUrl(value?.image?.asset?._ref) : null;
      const text = value?.text || "";

      return (
        <div className="rt-cornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)] prose-p:text-black">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <p className="mb-0 font-sans leading-none text-[2rem] translate-y-[.125rem]">{text}</p>
        </div>
      );
    },
    numberedList: ({ value }) => {
      const items = value?.items || [];
      const startNumber = value?.startNumber || 1;

      return (
        <ol
          className="flex flex-col items-start p-0 list-none numberedList mb-[1.25rem] gap-[1rem]"
          start={startNumber}>
          {items.map((item: any, index: number) => {
            return (
              <li
                key={index}
                className="flex items-center p-0 m-0 gap-[.625rem]">
                <span className="flex-shrink-0 font-sans text-[#D9F9B9] min-w-[2rem]">{startNumber + index}.</span>

                <div className="richtext richtext-start">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      );
    },
    highlightBlock: ({ value }) => {
      return (
        <div className="flex relative items-start w-full bg-white ml-[0.5rem] highlightBlock mb-[3rem] px-[1.75rem] py-[1.12rem] before:absolute before:top-0 before:left-[-0.5rem] before:w-[0.25rem] before:h-full before:bg-[#71A961]">
          <div className="flex-1 richtext richtext-start">
            <PortableText
              value={value?.text}
              components={richTextComponent}
            />
          </div>
        </div>
      );
    },
  },
};

export const customRichTextComponent = (props: PortableTextComponents) => {
  return merge(cloneDeep(richTextComponent), props);
};
